import { Divider, Flex, ListItem, UnorderedList } from '@chakra-ui/react';
import { AxiosError } from 'axios';
import { useShoppingCart } from 'context/shoppingCartContext';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useToast } from '@/hooks/useToast';

import { ApiErrorMessage } from '@/components/ApiErrors/ApiErrors';
import { H2 } from '@/components/Heading';
import { Button } from '@/components/UI/Buttons/Button';
import NavLink from '@/components/UI/Links/NavLink';

import BasketConfig from '@/constants/basket';
import RoutePath from '@/constants/route-path';
import { Product } from '@/models/api';
import { LocaleProps } from '@/models/props/LocalizationProps';
import { ProductListProps } from '@/models/props/ProductListProps';
import { ProductIdProps } from '@/models/props/ShoppingCartContextProps';
import { AppService, ProductService, ShoperService } from '@/services';

import { CartIcon, CheckIcon, DartBoardIcon, GiftIcon } from '../Icons';

export const RegisterEncourage = () => {
    const router = useRouter();
    const intl = useIntl();
    const { errorToast } = useToast();
    const [, setApiErrors] = useState<ApiErrorMessage[]>([]);
    const { cartItemsUnlogged, clearCartUnlogged } = useShoppingCart();
    const [isLoading, setIsLoading] = useState(false);
    const [continueWithShoperEnabled, setContinueWithShoperEnabled] = useState(false);

    const isFromCheckout = router.query.redirectURL === '/checkout';

    useEffect(() => {
        if (!isFromCheckout) {
            return;
        }

        const fetchShoperFlag = async () => {
            try {
                const isEnabled = await ShoperService.isContinueWithShoperEnabled();
                setContinueWithShoperEnabled(isEnabled);
            } catch (error) {
                setContinueWithShoperEnabled(false);
            }
        };

        fetchShoperFlag();
    }, [isFromCheckout]);

    const handleGuestCheckout = () => {
        window.dataLayer.push({ event: 'select_checkout_method', checkout_method: 'guest' });
        router.push(RoutePath.BasketNoAccount);
    };

    const handleShoperCheckout = () => {
        window.dataLayer.push({ event: 'select_checkout_method', checkout_method: 'guest' });
        setIsLoading(true);
        buyAsGuestDataLayer()
            .catch()
            .then(() => redirectToShoper())
            .catch((error) => {
                if (error instanceof AxiosError) {
                    setApiErrors(() => error?.response?.data?.errors);
                }
                errorToast({
                    description: intl.formatMessage({ id: 'api.errors.title' })
                });
            })
            .finally(() => setIsLoading(false));
    };

    const redirectToShoper = async () => {
        if (cartItemsUnlogged.length === 0) {
            router.push(RoutePath.Products);
            return;
        }

        const products = cartItemsUnlogged.map(({ code, quantity }) => ({
            code,
            quantity
        }));

        const { shoperBasketUrl } = await ProductService.getShoperBasketUrl(products);

        if (shoperBasketUrl) {
            await clearCartUnlogged();
            const url = new URL(shoperBasketUrl);
            const transactionId = localStorage.getItem('dataLayerPurchaseAsGuestTransactionId');
            if (transactionId) {
                url.searchParams.set('transaction_id', transactionId);
            }
            const finalUrl = url.toString();
            window.location.href = finalUrl;
            return;
        }

        router.push(RoutePath.Products);
    };

    const buyAsGuestDataLayer = async () => {
        let products: Product[] = [];
        if (cartItemsUnlogged.length) {
            const codes: ProductIdProps[] = cartItemsUnlogged.map((item) => item.code);
            const list: ProductListProps = await ProductService.getProductsList({
                productCodes: codes,
                pageSize: BasketConfig.pageSize,
                locale: intl.locale as LocaleProps
            });

            if (list) {
                products = list.items;
            } else {
                return;
            }
        }

        const currency = AppService.getInstanceCurrency();
        const brand = intl.formatMessage({
            id: `data-layer.brand.${process.env.NEXT_PUBLIC_SELL_ZONE}`
        });

        let value = 0;

        const itemsList = products.map((item: Product, index: number) => {
            const { code, categories, grossPrice, grossPriceBeforeDiscount, title } = item;
            const quantity = cartItemsUnlogged.find((i) => i.code === code)?.quantity || 1;
            const numberGrossPrice = Number(grossPrice?.replace(',', '.'));
            const numberGrossPriceBeforeDiscount = Number(
                grossPriceBeforeDiscount?.replace(',', '.')
            );
            const discount = Math.round(
                numberGrossPriceBeforeDiscount * 100 - numberGrossPrice * 100
            );
            const category = categories?.[0].name || '';

            value += numberGrossPrice * 100 * quantity;

            return {
                item_id: code,
                item_name: title,
                index: index + 1,
                discount: discount / 100,
                item_brand: brand,
                item_category: category,
                price: numberGrossPrice,
                quantity: quantity
            };
        });

        if (itemsList.length) {
            const transactionId = `T_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
            localStorage.setItem('dataLayerPurchaseAsGuestTransactionId', transactionId);
            window.dataLayer.push({ ecommerce: 'null' });
            window.dataLayer.push({
                event: 'purchase_as_guest',
                ecommerce: {
                    transaction_id: transactionId,
                    currency: currency,
                    value: value / 100,
                    items: itemsList
                }
            });
        }
    };

    return (
        <Flex gap={3} justifyContent="space-between" flexDirection="column" height="100%">
            {isFromCheckout && (
                <>
                    <Flex justify="center">
                        <Button
                            textAlign="center"
                            width="250px"
                            colorScheme="blue"
                            variant="solid"
                            fontWeight="semibold"
                            borderRadius={21}
                            px={4}
                            py={2}
                            isLoading={isLoading}
                            onClick={
                                continueWithShoperEnabled
                                    ? handleShoperCheckout
                                    : handleGuestCheckout
                            }
                            _hover={{
                                bgColor: 'blue.600'
                            }}
                        >
                            <FormattedMessage id="page.login.continue-as-guest" />
                        </Button>
                    </Flex>
                    <Flex alignItems="center" gap="2">
                        <Divider borderColor="gray.300" />
                        <FormattedMessage id="page.login.or" />
                        <Divider borderColor="gray.300" />
                    </Flex>
                </>
            )}
            <H2
                as="h2"
                textAlign="center"
                fontSize={{
                    base: '20px',
                    lg: '28px'
                }}
            >
                <FormattedMessage id="page.login.no-account" />
            </H2>
            <UnorderedList
                display="flex"
                flexDirection="column"
                gap="10px"
                pl={6}
                listStyleType="none"
                flex="1"
                justifyContent="center"
            >
                <ListItem display="flex" gap="2" alignItems="center">
                    <CheckIcon w="16px" />
                    <FormattedMessage id="page.login.encourage.list-item-1" />
                    <CartIcon pointerEvents="none" h="16px" w="auto" />
                </ListItem>
                <ListItem display="flex" gap="2" alignItems="center">
                    <CheckIcon w="16px" />
                    <FormattedMessage id="page.login.encourage.list-item-2" />
                    <GiftIcon pointerEvents="none" h="16px" w="auto" />
                </ListItem>
                <ListItem display="flex" gap="2" alignItems="center">
                    <CheckIcon w="16px" />
                    <FormattedMessage id="page.login.encourage.list-item-3" />
                    <DartBoardIcon pointerEvents="none" h="16px" w="auto" fill="#000000" />
                </ListItem>
            </UnorderedList>
            <Flex justify="center">
                <NavLink
                    width="250px"
                    textAlign="center"
                    variant="buttonBlue"
                    fontWeight="semibold"
                    mx="auto"
                    href={RoutePath.Register}
                    borderRadius={21}
                    px={4}
                    py={2}
                    _hover={{
                        bgColor: 'blue.600'
                    }}
                >
                    <FormattedMessage id="page.login.create-account" />
                </NavLink>
            </Flex>
        </Flex>
    );
};
